import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ExternalLink,
  RefreshCw,
  TrendingUp,
  DollarSign,
  Zap,
  Activity,
  BarChart3,
  AlertCircle,
  IndianRupee,
  Calendar,
  ArrowUpRight,
  Settings,
  AlertTriangle,
  Shield,
  ShieldAlert
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Legend, BarChart, Bar
} from "recharts";

const PROVIDER_ICONS = { openai: "O", anthropic: "A", google: "G", groq: "Q" };
const PROVIDER_COLORS = { openai: "#10A37F", anthropic: "#F97316", google: "#1D4ED8", groq: "#4338CA" };
const PROVIDER_NAMES = { openai: "OpenAI", anthropic: "Anthropic", google: "Google", groq: "Groq" };

function ProviderCard({ provider, onClick, budgetInfo }) {
  const hasKey = !!provider.key_status;
  const isValid = provider.key_status?.is_valid;
  const credit = provider.credit;
  const accent = provider.accent;

  let statusDot = "status-dot-neutral";
  let statusText = "No key";
  if (hasKey && isValid === true) { statusDot = "status-dot-success"; statusText = "Connected"; }
  else if (hasKey && isValid === false) { statusDot = "status-dot-error"; statusText = "Invalid key"; }
  else if (hasKey && isValid === null) { statusDot = "status-dot-warning"; statusText = "Not validated"; }

  const budgetStatus = budgetInfo?.status;
  const budgetPct = budgetInfo?.pct_used || 0;

  return (
    <Card
      data-testid="provider-card"
      className={`card-hover cursor-pointer provider-border-${provider.slug} relative overflow-hidden`}
      onClick={onClick}
    >
      {budgetStatus === "exceeded" && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-red-500" />
      )}
      {budgetStatus === "warning" && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500" />
      )}
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: accent }}>
              {PROVIDER_ICONS[provider.slug]}
            </div>
            <div>
              <h3 className="text-sm font-semibold">{provider.name}</h3>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className={`status-dot ${statusDot}`} data-testid="provider-card-status" />
                <span className="text-[11px] text-muted-foreground">{statusText}</span>
              </div>
            </div>
          </div>
          <a href={provider.billing_url} target="_blank" rel="noopener noreferrer"
            data-testid="provider-card-recharge-link"
            className="text-muted-foreground hover:text-foreground transition-colors"
            onClick={(e) => e.stopPropagation()}>
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
        <div className="mt-4">
          <p className="text-[11px] text-muted-foreground uppercase tracking-wider mb-1">Credit Balance</p>
          <p className="text-2xl font-semibold tabular-nums" data-testid="provider-card-balance">
            {credit ? `$${credit.balance.toFixed(2)}` : "--"}
          </p>
          {credit && (
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {credit.source === "manual" ? "Manually set" : "API synced"} · {new Date(credit.last_updated).toLocaleDateString()}
            </p>
          )}
        </div>
        {budgetInfo?.enabled && budgetInfo?.monthly_limit_usd > 0 && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-[11px] mb-1">
              <span className="text-muted-foreground">Monthly Budget</span>
              <span className={`font-medium ${budgetStatus === 'exceeded' ? 'text-red-600' : budgetStatus === 'warning' ? 'text-amber-600' : 'text-muted-foreground'}`}>
                ${budgetInfo.month_spend_usd?.toFixed(4)} / ${budgetInfo.monthly_limit_usd?.toFixed(2)}
              </span>
            </div>
            <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${budgetStatus === 'exceeded' ? 'bg-red-500' : budgetStatus === 'warning' ? 'bg-amber-500' : 'bg-primary'}`}
                style={{ width: `${Math.min(budgetPct, 100)}%` }}
              />
            </div>
            {budgetStatus === "exceeded" && (
              <div className="flex items-center gap-1 mt-1.5">
                <ShieldAlert className="w-3 h-3 text-red-600" />
                <span className="text-[10px] text-red-600 font-medium">Budget exceeded ({budgetPct.toFixed(0)}%)</span>
              </div>
            )}
            {budgetStatus === "warning" && (
              <div className="flex items-center gap-1 mt-1.5">
                <AlertTriangle className="w-3 h-3 text-amber-600" />
                <span className="text-[10px] text-amber-600 font-medium">Nearing limit ({budgetPct.toFixed(0)}%)</span>
              </div>
            )}
          </div>
        )}
        {!hasKey && (
          <div className="mt-3">
            <Badge variant="outline" className="text-[11px] gap-1"><AlertCircle className="w-3 h-3" /> Add key to get started</Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SpendingCard({ title, data, usdToInr, isActive }) {
  if (!data) return null;
  const providers = ["openai", "anthropic", "google", "groq"];

  return (
    <div className="space-y-4">
      {/* Total */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-primary/10 to-transparent rounded-xl">
        <div>
          <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Total Spent</p>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-3xl font-bold tabular-nums flex items-center gap-1">
              <IndianRupee className="w-5 h-5" />{data.total_cost_inr.toFixed(2)}
            </span>
            <span className="text-sm text-muted-foreground tabular-nums">(${data.total_cost_usd.toFixed(4)})</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {data.total_requests} requests · {data.total_tokens.toLocaleString()} tokens
          </p>
        </div>
      </div>

      {/* Per-provider breakdown */}
      <div className="grid grid-cols-2 gap-3">
        {providers.map(p => {
          const pData = data.by_provider?.[p];
          if (!pData) return null;
          return (
            <div key={p} className="p-3 rounded-lg border bg-white" style={{ borderLeft: `3px solid ${PROVIDER_COLORS[p]}` }}>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-5 h-5 rounded flex items-center justify-center text-white text-[10px] font-bold"
                  style={{ backgroundColor: PROVIDER_COLORS[p] }}>
                  {PROVIDER_ICONS[p]}
                </div>
                <span className="text-xs font-medium">{PROVIDER_NAMES[p]}</span>
              </div>
              <div className="flex items-baseline gap-1">
                <IndianRupee className="w-3 h-3 text-muted-foreground" />
                <span className="text-lg font-semibold tabular-nums">{pData.cost_inr.toFixed(2)}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] text-muted-foreground">${pData.cost_usd.toFixed(4)}</span>
                <span className="text-[10px] text-muted-foreground">·</span>
                <span className="text-[10px] text-muted-foreground">{pData.tokens.toLocaleString()} tok</span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[10px] text-muted-foreground">{pData.requests} req</span>
                {pData.avg_latency > 0 && (
                  <>
                    <span className="text-[10px] text-muted-foreground">·</span>
                    <span className="text-[10px] text-muted-foreground">{pData.avg_latency}s avg</span>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UsageChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <Card data-testid="dashboard-usage-chart">
        <CardHeader><CardTitle className="text-sm font-semibold tracking-tight">Cost Over Time (INR)</CardTitle></CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center">
          <div className="text-center">
            <BarChart3 className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Run your first request to see usage charts.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const pivoted = {};
  data.forEach((d) => {
    if (!pivoted[d.date]) pivoted[d.date] = { date: d.date, openai: 0, anthropic: 0, google: 0, groq: 0 };
    pivoted[d.date][d.provider] = (pivoted[d.date][d.provider] || 0) + (d.cost_inr || d.cost_usd * 85.5 || 0);
  });
  const chartData = Object.values(pivoted).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <Card data-testid="dashboard-usage-chart">
      <CardHeader><CardTitle className="text-sm font-semibold tracking-tight">Cost Over Time (INR)</CardTitle></CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorOpenai" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10A37F" stopOpacity={0.1}/><stop offset="95%" stopColor="#10A37F" stopOpacity={0}/></linearGradient>
              <linearGradient id="colorAnthropic" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#F97316" stopOpacity={0.1}/><stop offset="95%" stopColor="#F97316" stopOpacity={0}/></linearGradient>
              <linearGradient id="colorGoogle" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#1D4ED8" stopOpacity={0.1}/><stop offset="95%" stopColor="#1D4ED8" stopOpacity={0}/></linearGradient>
              <linearGradient id="colorGroq" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#4338CA" stopOpacity={0.1}/><stop offset="95%" stopColor="#4338CA" stopOpacity={0}/></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94A3B8" />
            <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" tickFormatter={(v) => `₹${v.toFixed(2)}`} />
            <RechartsTooltip contentStyle={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }}
              formatter={(value) => [`₹${Number(value).toFixed(4)}`, undefined]} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="openai" name="OpenAI" stroke="#10A37F" fill="url(#colorOpenai)" strokeWidth={2} />
            <Area type="monotone" dataKey="anthropic" name="Anthropic" stroke="#F97316" fill="url(#colorAnthropic)" strokeWidth={2} />
            <Area type="monotone" dataKey="google" name="Google" stroke="#1D4ED8" fill="url(#colorGoogle)" strokeWidth={2} />
            <Area type="monotone" dataKey="groq" name="Groq" stroke="#4338CA" fill="url(#colorGroq)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function RecentActivity({ logs }) {
  if (!logs || logs.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-sm font-semibold tracking-tight">Recent Activity</CardTitle></CardHeader>
        <CardContent className="flex items-center justify-center h-[260px]">
          <p className="text-sm text-muted-foreground">No activity yet.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader><CardTitle className="text-sm font-semibold tracking-tight">Recent Activity</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {logs.slice(0, 6).map((log, i) => (
          <div key={log.id || i} className="flex items-start gap-3 text-sm">
            <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: PROVIDER_COLORS[log.provider] || "#94A3B8" }} />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium truncate">
                {log.provider?.charAt(0).toUpperCase() + log.provider?.slice(1)} · {log.task_type} · <span className="font-mono">{log.model}</span>
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {log.status === "success" ? (
                  <span className="text-green-600">
                    {log.usage?.total_tokens || 0} tok · ₹{((log.estimated_cost || 0) * 85.5).toFixed(2)} · {log.latency}s
                  </span>
                ) : (<span className="text-red-500">Failed</span>)}
              </p>
            </div>
            <span className="text-[10px] text-muted-foreground whitespace-nowrap">
              {log.timestamp ? new Date(log.timestamp).toLocaleTimeString() : ""}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function Dashboard({ providers, loading, onRefresh, api }) {
  const [stats, setStats] = useState(null);
  const [spending, setSpending] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [newRate, setNewRate] = useState("");
  const [spendingPeriod, setSpendingPeriod] = useState("today");
  const [budgetLimits, setBudgetLimits] = useState({});
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [budgetProvider, setBudgetProvider] = useState("");
  const [budgetAmount, setBudgetAmount] = useState("");
  const [budgetThreshold, setBudgetThreshold] = useState("80");
  const [budgetEnabled, setBudgetEnabled] = useState(true);
  const [savingBudget, setSavingBudget] = useState(false);

  const fetchData = async () => {
    try {
      const [statsRes, logsRes, spendingRes, budgetRes] = await Promise.all([
        axios.get(`${api}/usage/stats`),
        axios.get(`${api}/usage?limit=10`),
        axios.get(`${api}/usage/spending`),
        axios.get(`${api}/budget-limits`),
      ]);
      setStats(statsRes.data);
      setRecentLogs(logsRes.data.logs || []);
      setSpending(spendingRes.data);
      setBudgetLimits(budgetRes.data || {});
    } catch (err) {
      console.error("Dashboard data fetch error:", err);
    }
  };

  useEffect(() => { fetchData(); }, [api]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchData(), onRefresh()]);
    setRefreshing(false);
    toast.success("Dashboard refreshed");
  };

  const handleUpdateRate = async () => {
    const rate = parseFloat(newRate);
    if (!rate || rate <= 0) { toast.error("Enter a valid rate"); return; }
    try {
      await axios.put(`${api}/exchange-rate`, { usd_to_inr: rate });
      toast.success(`Exchange rate updated to ₹${rate}`);
      setRateDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to update rate");
    }
  };

  const handleSaveBudget = async () => {
    if (!budgetProvider) { toast.error("Select a provider"); return; }
    const amount = parseFloat(budgetAmount);
    if (!amount || amount <= 0) { toast.error("Enter a valid budget amount"); return; }
    setSavingBudget(true);
    try {
      await axios.put(`${api}/budget-limits/${budgetProvider}`, {
        monthly_limit_usd: amount,
        alert_threshold_pct: parseFloat(budgetThreshold) || 80,
        enabled: budgetEnabled,
      });
      toast.success(`Budget limit set for ${PROVIDER_NAMES[budgetProvider]}: $${amount}/month`);
      setBudgetDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to update budget limit");
    } finally {
      setSavingBudget(false);
    }
  };

  const openBudgetDialog = (providerSlug) => {
    const existing = budgetLimits[providerSlug];
    setBudgetProvider(providerSlug);
    setBudgetAmount(existing?.monthly_limit_usd > 0 ? existing.monthly_limit_usd.toString() : "");
    setBudgetThreshold(existing?.alert_threshold_pct?.toString() || "80");
    setBudgetEnabled(existing?.enabled ?? true);
    setBudgetDialogOpen(true);
  };

  const usdToInr = spending?.usd_to_inr || stats?.usd_to_inr || 85.5;

  const totalRequests = stats ? Object.values(stats.by_provider).reduce((s, p) => s + p.total_requests, 0) : 0;
  const totalCostUsd = stats ? Object.values(stats.by_provider).reduce((s, p) => s + (p.total_cost_usd || 0), 0) : 0;
  const totalCostInr = totalCostUsd * usdToInr;
  const totalTokens = stats ? Object.values(stats.by_provider).reduce((s, p) => s + p.total_tokens, 0) : 0;

  const currentSpendingData = spending?.[spendingPeriod === "last_month" ? "last_month" : spendingPeriod === "this_month" ? "this_month" : spendingPeriod === "this_week" ? "this_week" : spendingPeriod === "all_time" ? "all_time" : "today"];

  return (
    <div className="main-content-inner">
      {/* Header */}
      <div className="header-wash -mx-8 -mt-6 px-8 pt-6 mb-6 rounded-b-2xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">Monitor your LLM providers, spending, and usage</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => openBudgetDialog("")} className="gap-1 text-xs" data-testid="budget-settings-btn">
              <Shield className="w-3 h-3" /> Budget
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setNewRate(usdToInr.toString()); setRateDialogOpen(true); }} className="gap-1 text-xs">
              <IndianRupee className="w-3 h-3" /> ₹{usdToInr}
            </Button>
            <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing} className="gap-2">
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>

        {/* Provider Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {loading
            ? Array(4).fill(0).map((_, i) => (<Card key={i}><CardContent className="p-5"><Skeleton className="h-32" /></CardContent></Card>))
            : providers.map((p, i) => (
                <motion.div key={p.slug} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <ProviderCard provider={p} onClick={() => window.location.href = "/keys"} budgetInfo={budgetLimits[p.slug]} />
                </motion.div>
              ))
          }
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="card-hover">
          <CardContent className="p-4 sm:p-5">
            <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Total Requests</p>
            <p className="text-2xl font-semibold mt-1 tabular-nums" style={{ color: "#0F766E" }}>{totalRequests.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 sm:p-5">
            <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Total Spent (INR)</p>
            <p className="text-2xl font-semibold mt-1 tabular-nums flex items-center gap-1" style={{ color: "#F97316" }}>
              <IndianRupee className="w-5 h-5" />{totalCostInr.toFixed(2)}
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5">${totalCostUsd.toFixed(4)} USD</p>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 sm:p-5">
            <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Total Tokens</p>
            <p className="text-2xl font-semibold mt-1 tabular-nums" style={{ color: "#1D4ED8" }}>{totalTokens.toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 sm:p-5">
            <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Per-LLM Cost (INR)</p>
            <div className="mt-1 space-y-1">
              {["openai", "anthropic", "google", "groq"].map(p => {
                const pStat = stats?.by_provider?.[p];
                const costInr = (pStat?.total_cost_usd || 0) * usdToInr;
                return (
                  <div key={p} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: PROVIDER_COLORS[p] }} />
                      <span className="text-muted-foreground">{PROVIDER_NAMES[p]}</span>
                    </div>
                    <span className="font-mono tabular-nums font-medium">₹{costInr.toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Unified Spending Panel */}
      <Card className="mb-6" data-testid="unified-spending-panel">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-sm font-semibold tracking-tight flex items-center gap-2">
              <IndianRupee className="w-4 h-4" /> Spending Breakdown
            </CardTitle>
            <div className="overflow-x-auto -mx-2 px-2">
              <Tabs value={spendingPeriod} onValueChange={setSpendingPeriod}>
                <TabsList className="h-8">
                  <TabsTrigger value="today" className="text-xs h-6 px-2 sm:px-3">Today</TabsTrigger>
                  <TabsTrigger value="this_week" className="text-xs h-6 px-2 sm:px-3">Week</TabsTrigger>
                  <TabsTrigger value="this_month" className="text-xs h-6 px-2 sm:px-3">Month</TabsTrigger>
                  <TabsTrigger value="last_month" className="text-xs h-6 px-2 sm:px-3">Last Mo.</TabsTrigger>
                  <TabsTrigger value="all_time" className="text-xs h-6 px-2 sm:px-3">All</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {currentSpendingData ? (
            <SpendingCard title={spendingPeriod} data={currentSpendingData} usdToInr={usdToInr} />
          ) : (
            <div className="text-center py-8">
              <IndianRupee className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No spending data yet. Run some requests to see breakdown.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Chart + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="col-span-full lg:col-span-8">
          <UsageChart data={stats?.daily || []} />
        </div>
        <div className="col-span-full lg:col-span-4">
          <RecentActivity logs={recentLogs} />
        </div>
      </div>

      {/* Exchange Rate Dialog */}
      <Dialog open={rateDialogOpen} onOpenChange={setRateDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Update USD to INR Exchange Rate</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Current Rate: 1 USD = ₹{usdToInr}</Label>
              <Input type="number" step="0.01" min="1" value={newRate} onChange={e => setNewRate(e.target.value)} placeholder="e.g. 85.50" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateRate}>Update Rate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Budget Limits Dialog */}
      <Dialog open={budgetDialogOpen} onOpenChange={setBudgetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-4 h-4" /> Budget Limits
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Provider selector or show all */}
            {!budgetProvider ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Set monthly spending caps per provider. You'll see warnings when approaching the limit.</p>
                {["openai", "anthropic", "google", "groq"].map(p => {
                  const bl = budgetLimits[p];
                  return (
                    <div key={p} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => {
                        setBudgetProvider(p);
                        setBudgetAmount(bl?.monthly_limit_usd > 0 ? bl.monthly_limit_usd.toString() : "");
                        setBudgetThreshold(bl?.alert_threshold_pct?.toString() || "80");
                        setBudgetEnabled(bl?.enabled ?? true);
                      }}>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded flex items-center justify-center text-white text-[10px] font-bold"
                          style={{ backgroundColor: PROVIDER_COLORS[p] }}>
                          {PROVIDER_ICONS[p]}
                        </div>
                        <span className="text-sm font-medium">{PROVIDER_NAMES[p]}</span>
                      </div>
                      <div className="text-right">
                        {bl?.enabled && bl?.monthly_limit_usd > 0 ? (
                          <div>
                            <span className="text-sm font-mono font-medium">${bl.monthly_limit_usd.toFixed(2)}/mo</span>
                            <span className={`text-[10px] ml-2 ${bl.status === 'exceeded' ? 'text-red-600' : bl.status === 'warning' ? 'text-amber-600' : 'text-muted-foreground'}`}>
                              ({bl.pct_used?.toFixed(0)}% used)
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Not set</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded flex items-center justify-center text-white text-[10px] font-bold"
                    style={{ backgroundColor: PROVIDER_COLORS[budgetProvider] }}>
                    {PROVIDER_ICONS[budgetProvider]}
                  </div>
                  <span className="text-sm font-semibold">{PROVIDER_NAMES[budgetProvider]}</span>
                  <button className="text-xs text-primary hover:underline ml-auto" onClick={() => setBudgetProvider("")}>Back to all</button>
                </div>
                <div className="space-y-2">
                  <Label>Monthly Budget Limit (USD)</Label>
                  <Input type="number" step="0.01" min="0" placeholder="e.g. 10.00"
                    value={budgetAmount} onChange={e => setBudgetAmount(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Alert Threshold (%)</Label>
                  <Input type="number" step="5" min="10" max="100" placeholder="80"
                    value={budgetThreshold} onChange={e => setBudgetThreshold(e.target.value)} />
                  <p className="text-[11px] text-muted-foreground">You'll see a warning when spending reaches this % of the limit.</p>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="budget-enabled" checked={budgetEnabled}
                    onChange={e => setBudgetEnabled(e.target.checked)} className="rounded border-input" />
                  <Label htmlFor="budget-enabled" className="text-sm cursor-pointer">Enable budget monitoring</Label>
                </div>
                {budgetLimits[budgetProvider]?.month_spend_usd > 0 && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Current month spend: <span className="font-mono font-medium text-foreground">${budgetLimits[budgetProvider].month_spend_usd.toFixed(4)}</span> ({budgetLimits[budgetProvider].month_requests} requests)</p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            {budgetProvider ? (
              <>
                <Button variant="outline" onClick={() => setBudgetProvider("")}>Back</Button>
                <Button onClick={handleSaveBudget} disabled={savingBudget}>
                  {savingBudget ? "Saving..." : "Save Budget"}
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setBudgetDialogOpen(false)}>Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
